/**
 * Sprint 40 — resolução config-driven dos defaults de um país.
 *
 * Funde o catálogo dinâmico `/config/markets/items/{country}` (campos opcionais)
 * sobre os defaults COMPILADOS (`countryDefaults`, `phone`, `currencyForCountry`).
 * É o que permite onboardar um país 100%-novo (ex.: UE via Paddle) sem release:
 * para BR/MX/US/CA/IT o catálogo geralmente está ausente e tudo cai no compilado
 * (comportamento idêntico ao histórico); para um país novo, o catálogo é a fonte.
 *
 * Puro (sem Firestore) para teste; o hook web (`useMarketConfig`) e o servidor
 * fiam o snapshot e passam os campos crus aqui.
 *
 * `currency` NÃO é override do catálogo: moeda é fechada em 3 (BRL/EUR/USD —
 * memória `project_currencies_locked_3`), então sempre vem de `currencyForCountry`.
 */
import {
  ALL_COUNTRIES,
  type Country,
  type Currency,
  currencyForCountry,
} from './enums';
import { localeForCountry, timeZonesForCountry } from './countryDefaults';
import { PhoneFormatter } from './phone';
import { TenantLocaleSchema, type TenantLocale } from './schemas';

const KNOWN_COUNTRIES = new Set<string>(ALL_COUNTRIES);
const KNOWN_LOCALES = new Set<string>(TenantLocaleSchema.options);
const DIAL_CODE_RE = /^\+\d{1,4}$/;

/** Locale fallback para país novo sem `language` no catálogo. */
const DEFAULT_LOCALE: TenantLocale = 'en';

function isKnownCountry(code: string): code is Country {
  return KNOWN_COUNTRIES.has(code);
}

function asLocale(value: unknown): TenantLocale | null {
  return typeof value === 'string' && KNOWN_LOCALES.has(value) ? (value as TenantLocale) : null;
}

function asDialCode(value: unknown): string | null {
  return typeof value === 'string' && DIAL_CODE_RE.test(value) ? value : null;
}

function asNonEmptyTimeZones(value: unknown): readonly string[] | null {
  if (!Array.isArray(value)) return null;
  const zones = value.filter((z): z is string => typeof z === 'string' && z.length > 0);
  return zones.length > 0 ? zones : null;
}

/** Defaults resolvidos de um país, prontos pro onboarding/UI. */
export type ResolvedMarketConfig = {
  currency: Currency;
  language: TenantLocale;
  /** Fusos IANA em ordem de uso; `[]` para país novo sem fusos configurados. */
  timeZones: readonly string[];
  /** DDI internacional (ex.: '+55'); `null` para país novo sem DDI no catálogo. */
  dialCode: string | null;
};

/**
 * Campos do doc `/config/markets/items/{country}` que sobrescrevem os defaults
 * compilados. Cru (vindo do Firestore) — validado individualmente aqui, nunca
 * confiado direto. `currency` de propósito não está: moeda é compilada.
 */
export type MarketCatalogOverrides = {
  language?: unknown;
  timezones?: unknown;
  phone_country_code?: unknown;
};

/**
 * Resolve os defaults de um país fundindo catálogo sobre compilado.
 * - `currency`: sempre `currencyForCountry(code)` (locked em 3).
 * - `language`: catálogo válido > compilado (países conhecidos) > 'en'.
 * - `timeZones`: catálogo não-vazio > compilado > [].
 * - `dialCode`: catálogo válido > compilado > null.
 */
export function resolveMarketConfig(
  countryCode: string,
  catalog?: MarketCatalogOverrides | null,
): ResolvedMarketConfig {
  const known = isKnownCountry(countryCode);

  const language =
    asLocale(catalog?.language) ?? (known ? localeForCountry(countryCode) : DEFAULT_LOCALE);

  const timeZones =
    asNonEmptyTimeZones(catalog?.timezones) ?? (known ? timeZonesForCountry(countryCode) : []);

  const dialCode =
    asDialCode(catalog?.phone_country_code) ??
    (known ? PhoneFormatter.dialCodeFor(countryCode) : null);

  return {
    currency: currencyForCountry(countryCode),
    language,
    timeZones,
    dialCode,
  };
}
